import React from "react";
import { SkeletonCard } from "./Skeleton";
import type { LoadPhase } from "./useLoadPhase";

/**
 * The strip's own footprint, so the block that stands in for it is the size of
 * the strip that lands in its place.
 *
 * One line's worth, which is all the strip holds.
 *
 * A floor for the strip itself rather than a fixed height, the same bargain
 * `WelcomeCard` makes. On a narrow screen the sentence takes a second line, and
 * the strip is free to grow into it: a height that clipped it would be a worse
 * trade than a block occasionally shorter than what replaces it.
 */
const FOOTPRINT = "h-[44px] 3xl:h-[52px]";
const FLOOR = "min-h-[44px] 3xl:min-h-[52px]";

/**
 * The warm ground, its border, and the mark that opens the line. The trio is
 * the design's own and is used nowhere else in the app — it marks this as
 * something outstanding rather than something wrong, which a red would say
 * instead. The mark is the one thing on the strip drawn in the accent, which is
 * what makes it read as a flag rather than as punctuation.
 */
const GROUND = "#FDF6EF";
const BORDER = "#FFD1C5";
const MARK = "#F04E23";

export interface SetupBannerProps {
  /** The one line the strip carries — what is outstanding. */
  title: string;
}

/**
 * The strip a screen carries while the record behind it has not been filled in:
 * that something is outstanding.
 *
 * It says the same thing however far through the form its reader is. There is
 * nothing on it to press and no figure on it: the form it sits over is the
 * thing to act on, and a percentage that moved as the fields were filled was a
 * second running commentary on a page that already shows what is in it.
 *
 * Whether it belongs on the screen at all is the page's to decide, and both
 * pages that carry one decide it on the saved record rather than on what is
 * typed — a record is set up once what is stored is, so the save is what takes
 * this away. See `MyBrandPage` and `SettingsPage`.
 *
 * The colours are inline styles rather than classes because they are one-off
 * values that appear nowhere else in the app's palette.
 */
export const SetupBanner: React.FC<SetupBannerProps> = ({ title }) => (
  <section
    style={{ backgroundColor: GROUND, borderColor: BORDER }}
    className={`mt-3 flex-shrink-0 rounded-[6px] border px-4 py-1.5 flex items-center gap-2 ${FLOOR}`}
  >
    {/* Decoration, not content: the sentence beside it already says that
        something is outstanding, and a screen reader announcing a bare "!"
        would be reading out the styling. */}
    <span
      aria-hidden="true"
      style={{ color: MARK }}
      className="flex-shrink-0 text-[18px] 3xl:text-[20px] font-bold leading-none"
    >
      !
    </span>

    <p className="min-w-0 text-[13px] sm:text-[14px] 3xl:text-[15px] font-semibold text-ink">
      {title}
    </p>
  </section>
);

export interface SetupBannerSkeletonProps {
  /**
   * How long the record behind the strip has been coming. Only `slow` draws the
   * block; while it is merely `waiting` the strip's space is held open and left
   * blank, so a record that arrives quickly goes straight to the answer without
   * a shimmer crossing the top of the screen first.
   */
  phase?: LoadPhase;
}

/**
 * The strip while the record behind it is being fetched.
 *
 * It shimmers on the same phase as the cards below it, so they land together
 * rather than the page filling in from the top down.
 *
 * The same `mt-3` the strip carries, so nothing above or below it moves as the
 * one takes the other's place.
 *
 * `rounded-[6px]` is the same corner the strip itself carries, so the block
 * standing in for it is cut the same way.
 */
export const SetupBannerSkeleton: React.FC<SetupBannerSkeletonProps> = ({
  phase,
}) => (
  <SkeletonCard
    phase={phase}
    className={`mt-3 flex-shrink-0 rounded-[6px] ${FOOTPRINT}`}
  />
);

export default SetupBanner;
